import { getPathResolver, PathResolve } from '@Components/pathresolver';
import React, { useEffect, useRef, useState } from 'react';
import s from './style.module.css';
import logo from '@Images/logo.webp';
import contactIcon from '@Images/contact.svg';
import { URL } from '@Pages/url';
import LazyableImage from '@Components/lazyable_image/view';
import BottomTriangleSVGIcon from '@Components/svg_icon/bottom_triangle';
import TestLabel from '@Components/test_label/view';

const rootPath = '/';

type HeaderProps = {
    courseId?: string;
    isInquiry?: boolean;
    // SSR時のgetPathResolverに渡す実際のリクエストパス。呼び出し元（Page）が
    // railsContext.pathname由来のcurrentPathを渡す（PR #2082 Point2対応）
    currentPath?: string;
};

export const Header = (props: HeaderProps) => {
    const contactUrl = props.courseId ? `${URL.CONTACT}?course_id=${props.courseId}` : URL.CONTACT;
    return (
        <div className={s.header}>
            <TestLabel />
            <div className={s.logo}>
                <a href={rootPath}>
                    <LazyableImage src={logo} alt='住地ゴルフ' notLazy={true} width={155} height={23} />
                </a>
            </div>
            {props.isInquiry || (
                <>
                    <HeaderMenu title={'ホーム'} path={getPathResolver('home', props.currentPath)} />
                    <HeaderDropdown
                        title={'ゴルフ会員権情報'}
                        items={[
                            { title: '会員権相場検索', path: getPathResolver('search', props.currentPath) },
                            { title: '会員権の基礎知識', path: getPathResolver('knowledge', props.currentPath) },
                            {
                                title: '会員権の特集記事',
                                path: getPathResolver('featured_staff_blog', props.currentPath),
                            },
                        ]}
                    />
                    <HeaderMenu title={'法人の\nお客様へ'} path={getPathResolver('corporate', props.currentPath)} />
                    <HeaderMenu title={'お客様の声'} path={getPathResolver('member_voice', props.currentPath)} />
                    <HeaderMenu
                        title={'ゴルフ場\nインタビュー'}
                        path={getPathResolver('course_interviews', props.currentPath)}
                    />
                    <HeaderDropdown
                        title={'住地ゴルフ\nについて'}
                        align='right'
                        items={[
                            { title: '会社案内', path: getPathResolver('about_membership', props.currentPath) },
                            { title: '事業紹介', path: getPathResolver('service', props.currentPath) },
                        ]}
                    />
                    <HeaderMenu title={'住地倶楽部'} path={getPathResolver('club', props.currentPath)} />
                    <div className={s.header_menu}>
                        <div className={s.header_menu_text}>
                            <a href={contactUrl}>
                                <LazyableImage src={contactIcon} alt='お問合せ' width={20} height={18} notLazy={true} />
                                お問合せ
                            </a>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

interface HeaderMenuProps {
    title: string;
    path: PathResolve;
}
const renderTitleLines = (title: string) =>
    title.split('\n').map((t, i) => (
        <p className={`${s.margin_0} ${s.padding_0} ${s.multiline_label}`} key={i}>
            {t}
        </p>
    ));

const HeaderMenu = (props: HeaderMenuProps) => {
    const title = renderTitleLines(props.title);
    return (
        <a href={props.path.url} className={`${s.header_menu} ${props.path.current ? s.current : ''}`}>
            <div className={s.header_menu_text}>{title}</div>
        </a>
    );
};

interface HeaderDropdownItem {
    title: string;
    path: PathResolve;
}
interface HeaderDropdownProps {
    title: string;
    items: HeaderDropdownItem[];
    align?: 'left' | 'right';
}
const HeaderDropdown = (props: HeaderDropdownProps) => {
    const [hoverOpen, setHoverOpen] = useState(false);
    const [clickOpen, setClickOpen] = useState(false);
    const open = hoverOpen || clickOpen;
    const current = props.items.some((item) => item.path.current);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const closeIfOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setHoverOpen(false);
                setClickOpen(false);
            }
        };
        document.addEventListener('click', closeIfOutside);
        return () => document.removeEventListener('click', closeIfOutside);
    }, [open]);

    return (
        <div
            ref={wrapperRef}
            className={`${s.header_menu} ${s.header_dropdown_wrapper} ${current ? s.current : ''}`}
            onMouseEnter={() => setHoverOpen(true)}
            onMouseLeave={() => {
                setHoverOpen(false);
                setClickOpen(false);
            }}
        >
            <button
                type='button'
                className={s.header_menu_text}
                onClick={() => {
                    if (open) {
                        setHoverOpen(false);
                        setClickOpen(false);
                    } else {
                        setClickOpen(true);
                    }
                }}
                aria-expanded={open}
            >
                <div>{renderTitleLines(props.title)}</div>
                <BottomTriangleSVGIcon
                    className={`${s.header_dropdown_caret} ${open ? s.header_dropdown_caret_open : ''}`}
                    front_fill='#464646'
                />
            </button>
            {open && (
                <div className={`${s.header_dropdown} ${props.align === 'right' ? s.header_dropdown_right : ''}`}>
                    {props.items.map((item) => (
                        <a key={item.path.url} href={item.path.url} className={s.header_dropdown_link}>
                            {item.title}
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Header;
